import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Ticket, ArrowRight } from "lucide-react";
import { inIframe, openFullscreen, useEmbedAutoHeight } from "@/lib/embed";

const LOGO = "https://www.areteteatro.com/wp-content/uploads/2020/03/logo-arete.png";

const fmtDate = (d) => new Date(d).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export default function TrialsPublic() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState(() => {
    try { const c = sessionStorage.getItem("public_slots"); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [l, setL] = useState({ email: "", password: "", phone: "" });
  const [r, setR] = useState({ name: "", email: "", password: "", phone: "" });

  useEffect(() => {
    api.get("/public/slots")
      .then(({ data }) => { setSlots(data); try { sessionStorage.setItem("public_slots", JSON.stringify(data)); } catch (e) {} })
      .catch(() => setSlots((s) => (s ?? [])));
  }, []);

  useEmbedAutoHeight();

  useEffect(() => {
    if (!slots || inIframe) return;
    const id = new URLSearchParams(window.location.search).get("book");
    if (id) { const s = slots.find((x) => x.id === id); if (s && !s.full) onSlotClick(s); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const bookNow = async (slotId, phone) => {
    try { await api.post("/bookings", { slot_id: slotId, phone }); toast.success("Lezione di prova prenotata!"); }
    catch (err) { toast.warning(apiError(err.response?.data?.detail)); }
    navigate("/dashboard");
  };

  const onSlotClick = (slot) => {
    if (inIframe) return openFullscreen(`/prove?book=${slot.id}`);
    if (user) {
      if (user.phone) return bookNow(slot.id, user.phone);
      toast.info("Completa la prenotazione dalla tua area personale indicando il telefono.");
      return navigate("/dashboard");
    }
    setSelected(slot);
    setL({ email: "", password: "", phone: "" });
    setR({ name: "", email: "", password: "", phone: "" });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(r.name, r.email, r.password, r.phone || "", true);
      await api.post("/bookings", { slot_id: selected.id, phone: r.phone });
      toast.success("Registrazione e prenotazione completate!");
      navigate("/dashboard");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(l.email, l.password);
      await api.post("/bookings", { slot_id: selected.id, phone: l.phone });
      toast.success("Prenotazione confermata!");
      navigate("/dashboard");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-black">
        <div className="max-w-5xl mx-auto px-6 py-8 sm:py-12">
          <div className="flex items-center gap-2 mb-5"><span className="h-px w-8 bg-primary" /><span className="text-xs tracking-[0.3em] uppercase text-white/70">Scuola di Teatro · Torino</span></div>
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Areté Teatro" className="h-16 w-16 rounded-full bg-white p-1 object-contain shrink-0" />
            <div>
              <h1 className="text-3xl sm:text-5xl text-white leading-tight">Lezioni di prova <span className="text-primary">gratuite</span></h1>
              <p className="text-white/70 mt-2 text-base sm:text-lg">Scegli la data che preferisci: bastano pochi secondi per prenotare il tuo posto.</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 fade-up">
        {slots === null ? (
          <>
            <div className="flex items-center gap-2 mb-6"><Ticket className="text-primary" size={20} /><h2 className="text-2xl">Date disponibili</h2></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="slots-skeleton">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-5 animate-pulse">
                  <div className="h-5 w-2/3 bg-muted rounded mb-3" />
                  <div className="h-4 w-1/2 bg-muted rounded mb-2" />
                  <div className="h-4 w-1/3 bg-muted rounded mb-5" />
                  <div className="h-9 w-full bg-muted rounded" />
                </div>
              ))}
            </div>
          </>
        ) : slots.length === 0 ? (
          <Card className="p-10 bg-card border-border text-center" data-testid="no-slots">
            <Ticket className="mx-auto text-muted-foreground mb-3" size={30} />
            <p className="text-lg mb-1">Nessuna data disponibile al momento</p>
            <p className="text-muted-foreground text-sm">Torna presto: pubblicheremo qui le prossime lezioni di prova gratuite.</p>
          </Card>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-6"><Ticket className="text-primary" size={20} /><h2 className="text-2xl">Date disponibili</h2></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-slots-grid">
              {slots.map((s) => {
                const full = s.full;
                return (
                  <Card key={s.id} className="p-5 bg-card border-border flex flex-col hover:border-primary/50 hover:shadow-md transition-all" data-testid={`public-slot-${s.id}`}>
                    <div className="flex items-center gap-2 text-primary mb-2"><CalendarDays size={16} /><span className="font-medium text-foreground capitalize">{fmtDate(s.date)}</span></div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mb-1"><Clock size={14} /> {s.time}</p>
                    {s.location && <p className="text-sm text-muted-foreground flex items-center gap-2 mb-1"><MapPin size={14} /> {s.location}</p>}
                    <div className="mb-4" />
                    <Button data-testid={`public-book-${s.id}`} disabled={full} onClick={() => onSlotClick(s)} className="mt-auto w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                      {full ? "Prenotazioni chiuse" : <>Prenota questa data <ArrowRight size={16} className="ml-1.5" /></>}
                    </Button>
                  </Card>
                );
              })}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-8">Hai già un account? <button className="text-primary underline underline-offset-2" data-testid="go-login" onClick={() => (inIframe ? openFullscreen("/login") : navigate("/login"))}>Accedi all'area riservata</button></p>
          </>
        )}
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle>Prenota la tua lezione di prova</DialogTitle>
            {selected && (
              <DialogDescription>
                Stai prenotando: <strong className="text-foreground capitalize">{fmtDate(selected.date)}</strong> alle <strong className="text-foreground">{selected.time}</strong>{selected.location ? ` · ${selected.location}` : ""}.
                <br />Crea un account (o accedi) per confermare: la prenotazione verrà registrata subito.
              </DialogDescription>
            )}
          </DialogHeader>

          <Tabs defaultValue="register">
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="register" data-testid="dialog-tab-register">Registrati</TabsTrigger>
              <TabsTrigger value="login" data-testid="dialog-tab-login">Ho già un account</TabsTrigger>
            </TabsList>
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-3">
                <div><Label>Nome e Cognome</Label><Input data-testid="dialog-reg-name" required value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="mt-1.5 bg-white" placeholder="Mario Rossi" /></div>
                <div><Label>Email</Label><Input data-testid="dialog-reg-email" type="email" required value={r.email} onChange={(e) => setR({ ...r, email: e.target.value })} className="mt-1.5 bg-white" placeholder="tua@email.com" /></div>
                <div><Label>Password</Label><PasswordInput data-testid="dialog-reg-password" required minLength={6} value={r.password} onChange={(e) => setR({ ...r, password: e.target.value })} className="mt-1.5 bg-white" placeholder="Almeno 6 caratteri" /></div>
                <div><Label>Telefono <span className="text-primary">*</span></Label><Input data-testid="dialog-reg-phone" type="tel" required value={r.phone} onChange={(e) => setR({ ...r, phone: e.target.value })} className="mt-1.5 bg-white" placeholder="333 1234567" /><p className="text-xs text-muted-foreground mt-1">Usato esclusivamente per contattarti sulla logistica della lezione.</p></div>
                <Button data-testid="dialog-reg-submit" disabled={busy} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">{busy ? "Attendere…" : "Crea account e prenota"}</Button>
              </form>
            </TabsContent>
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-3">
                <div><Label>Email</Label><Input data-testid="dialog-login-email" type="email" required value={l.email} onChange={(e) => setL({ ...l, email: e.target.value })} className="mt-1.5 bg-white" placeholder="tua@email.com" /></div>
                <div><Label>Password</Label><PasswordInput data-testid="dialog-login-password" required value={l.password} onChange={(e) => setL({ ...l, password: e.target.value })} className="mt-1.5 bg-white" placeholder="••••••••" /></div>
                <div><Label>Telefono <span className="text-primary">*</span></Label><Input data-testid="dialog-login-phone" type="tel" required value={l.phone} onChange={(e) => setL({ ...l, phone: e.target.value })} className="mt-1.5 bg-white" placeholder="333 1234567" /><p className="text-xs text-muted-foreground mt-1">Usato esclusivamente per la logistica di contatto.</p></div>
                <Button data-testid="dialog-login-submit" disabled={busy} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">{busy ? "Attendere…" : "Accedi e prenota"}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
